
import React, { useCallback, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { Eye, ExternalLink, Bell, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useNotifications } from '@/hooks/use-notifications';
import { useRequiredActions } from '@/hooks/use-required-actions';
import { WearableReauthNotification } from './WearableReauthNotification';
import { cn } from '@/lib/utils';

interface NotificationPanelProps {
  /** Whether to enable auto-mark-as-read when notifications are visible. Defaults to false for popup usage. */
  enableAutoRead?: boolean;
}

export const NotificationPanel: React.FC<NotificationPanelProps> = ({ enableAutoRead = false }) => {
  const { notifications, loading, unreadCount, markAsRead, markAllAsRead, forceRefresh } = useNotifications();
  const [isClearing, setIsClearing] = React.useState(false);
  const { requiredActions, hasIncompleteActions } = useRequiredActions();
  const observerRef = useRef<IntersectionObserver | null>(null);
  const cardRefs = useRef<Map<string, HTMLElement>>(new Map());
  const navigate = useNavigate();

  // Set up intersection observer to auto-mark notifications as read when visible
  useEffect(() => {
    // Only enable auto-read if explicitly requested (e.g., on full notifications page)
    if (!enableAutoRead || loading || notifications.length === 0) return;

    // Create intersection observer
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const notificationId = entry.target.getAttribute('data-notification-id');
            const notification = notifications.find(n => n.id === notificationId);
            
            if (notification && !notification.is_read) {
              // Mark as read with a small delay to ensure user sees it
              setTimeout(() => {
                markAsRead(notification.id);
              }, 1000);
            }
          }
        });
      },
      {
        threshold: 0.5, // 50% of the card must be visible
        rootMargin: '0px'
      }
    );

    // Observe all notification cards
    cardRefs.current.forEach((element) => {
      if (observerRef.current) {
        observerRef.current.observe(element);
      }
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [enableAutoRead, notifications, loading, markAsRead]);

  const setCardRef = useCallback((notificationId: string, element: HTMLElement | null) => {
    if (element) {
      cardRefs.current.set(notificationId, element);
    } else {
      cardRefs.current.delete(notificationId);
    }
  }, []);

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'urgent':
        return '🚨';
      case 'booking':
        return '📅';
      case 'calendar_issue':
        return '⚠️';
      case 'wearable_issue':
        return '⌚';
      case 'system':
        return '🔧';
      case 'required_action':
        return '⚡';
      default:
        return 'ℹ️';
    }
  };

  const getNotificationVariant = (type: string) => {
    switch (type) {
      case 'urgent':
        return 'destructive';
      case 'booking':
        return 'default';
      case 'calendar_issue':
      case 'wearable_issue':
        return 'warning';
      case 'required_action':
        return 'destructive';
      default:
        return 'secondary';
    }
  };

  const handleCardClick = (notification: any) => {
    // If there's an action URL, navigate to it instead of just marking as read
    // For booking notifications, this will go to /bookings/:id and auto-open the detail sheet
    if (notification.action_url) {
      navigate(notification.action_url);
      return;
    }
    
    // Fallback: mark as read for notifications without action URLs
    if (!notification.is_read) {
      markAsRead(notification.id);
    }
  };

  const handleActionClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handleMarkAsReadClick = (e: React.MouseEvent, notificationId: string) => {
    e.stopPropagation();
    markAsRead(notificationId);
  };

  // Show only recent unread notifications (last 5)
  const recentNotifications = notifications.filter(n => !n.is_read).slice(0, 5);

  if (loading) {
    return (
      <div className="w-80 p-4">
        <div className="animate-pulse space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-12 bg-gray-200 rounded-lg"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="w-80 max-w-sm flex flex-col max-h-96">
      <div className="p-3 border-b flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bell className="h-4 w-4" />
            <h3 className="font-semibold text-sm">Notifications</h3>
            {unreadCount > 0 && (
              <Badge variant="secondary" className="text-xs h-5 px-1.5">
                {unreadCount}
              </Badge>
            )}
          </div>
          <div className="flex gap-1">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={forceRefresh}
              className="text-xs h-6 px-2"
              title="Refresh notifications"
            >
              <RefreshCw className="h-3 w-3" />
            </Button>
            {recentNotifications.length > 0 && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={async () => {
                  setIsClearing(true);
                  await markAllAsRead();
                  setTimeout(() => setIsClearing(false), 1000);
                }}
                disabled={isClearing}
                className="text-xs h-6 px-2"
                title="Mark all notifications as read"
              >
                {isClearing ? 'Clearing...' : 'Clear All'}
              </Button>
            )}
          </div>
        </div>
      </div>

      <ScrollArea className="flex-1 min-h-0 overflow-y-auto">
        <div className="p-2">
          {/* Required Actions Section */}
          {hasIncompleteActions && (
            <div className="mb-3">
              <div className="flex items-center gap-1.5 mb-2">
                <div className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse"></div>
                <h3 className="font-semibold text-xs text-red-600">Required Actions</h3>
              </div>
              <div className="space-y-1.5">
                {requiredActions.map((action) => (
                  <Card 
                    key={action.id}
                    className="border-red-200 bg-red-50/30 hover:shadow-md hover:scale-[1.02] transition-all duration-200 cursor-pointer active:scale-[0.98]"
                    onClick={() => navigate(action.action_url)}
                  >
                    <CardContent className="p-2.5">
                      <div className="flex gap-2">
                        <span className="text-sm flex-shrink-0 mt-0.5">⚡</span>
                        
                        <div className="flex-1 min-w-0 space-y-1">
                          <div className="flex items-start justify-between gap-2">
                            <h4 className="font-medium text-xs text-red-900 leading-tight line-clamp-2">
                              {action.title}
                            </h4>
                            <Badge variant="destructive" className="text-xs flex-shrink-0 h-4 px-1">
                              Required
                            </Badge>
                          </div>
                          
                          <p className="text-xs text-red-700 leading-relaxed line-clamp-2">
                            {action.message}
                          </p>
                          
                          <div className="flex items-center justify-between pt-0.5">
                            <span className="text-xs text-red-600 font-medium">
                              Action Required
                            </span>
                            
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-5 px-1.5 text-xs border-red-300 text-red-700 hover:bg-red-100"
                            >
                              {action.action_label}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {recentNotifications.length === 0 ? (
            <div className="p-4 text-center text-gray-500">
              <Bell className="h-6 w-6 mx-auto mb-2 text-gray-400" />
              {hasIncompleteActions ? (
                <p className="text-xs">No other notifications</p>
              ) : (
                <p className="text-xs">No unread notifications</p>
              )}
              {notifications.length > 0 && !hasIncompleteActions && (
                <p className="text-xs text-gray-400 mt-1">All caught up!</p>
              )}
            </div>
          ) : (
            <div className="space-y-1.5">
              {recentNotifications.map((notification) => {
                // Use specialized component for wearable re-auth notifications
                if (notification.type === 'wearable_issue' && notification.data?.requires_reauth) {
                  return (
                    <div 
                      key={notification.id}
                      ref={(el) => setCardRef(notification.id, el)}
                      data-notification-id={notification.id}
                    >
                      <WearableReauthNotification
                        notification={notification}
                        onMarkAsRead={markAsRead}
                      />
                    </div>
                  );
                }
                
                // Default notification component for all other notifications
                return (
                  <Card 
                    key={notification.id} 
                    ref={(el) => setCardRef(notification.id, el)}
                    data-notification-id={notification.id}
                    className={cn(
                      "transition-all duration-200 hover:shadow-md hover:scale-[1.02] cursor-pointer",
                      !notification.is_read && "bg-blue-50/50 border-blue-200",
                      "active:scale-[0.98]"
                    )}
                    onClick={() => handleCardClick(notification)}
                  >
                    <CardContent className="p-2.5">
                      <div className="flex gap-2">
                        <span className="text-sm flex-shrink-0 mt-0.5">
                          {getNotificationIcon(notification.type)}
                        </span>
                        
                        <div className="flex-1 min-w-0 space-y-1">
                          <div className="flex items-start justify-between gap-2">
                            <h4 className="font-medium text-xs text-gray-900 leading-tight line-clamp-3">
                              {notification.title}
                            </h4>
                            <Badge 
                              variant={getNotificationVariant(notification.type) as any}
                              className="text-xs flex-shrink-0 h-4 px-1"
                            >
                              {notification.type.replace('_', ' ')}
                            </Badge>
                          </div>
                          
                          <p className="text-xs text-gray-600 leading-relaxed line-clamp-3">
                            {notification.message}
                          </p>
                          
                          <div className="flex items-center justify-between pt-0.5">
                            <span className="text-xs text-gray-500">
                              {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                            </span>
                            
                            <div className="flex gap-0.5">
                              {notification.action_url && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  asChild
                                  className="h-5 px-1.5 text-xs"
                                  onClick={handleActionClick}
                                >
                                  <Link to={notification.action_url}>
                                    <ExternalLink className="h-3 w-3" />
                                  </Link>
                                </Button>
                              )}
                              
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => handleMarkAsReadClick(e, notification.id)}
                                className="h-5 px-1.5 text-xs"
                                title="Mark as read"
                              >
                                <Eye className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </ScrollArea>

      {notifications.length > 0 && (
        <div className="p-2 border-t flex-shrink-0">
          <Button 
            variant="outline" 
            size="sm" 
            className="w-full text-xs h-7"
            asChild
          >
            <Link to="/notifications">
              View all notifications
            </Link>
          </Button>
        </div>
      )}
    </div>
  );
};
